import { memo } from 'react';
import { Handle, Position, useStore } from '@xyflow/react';
import {
    Clock,
    Timer,
    Calendar,
    Settings,
    Trash2,
    Copy,
    MousePointerClick,
    Link as LinkIcon,
    Hourglass,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { FlowNodeProps } from '@/types/flow';
import type { ButtonData } from '@/types/flow';

/**
 * Wait For Click — node que pausa o flow até o lead clicar num botão URL
 * do node anterior (source) ou até o timeout disparar.
 *
 * Handles de saída são dinâmicos: lê o source (via useStore) e cria 1
 * handle por botão URL + 1 handle "não clicou" (timeout).
 *
 * `sourceHandle` segue o padrão do projeto:
 *   - `button-{id}`  → branch daquele botão URL (mapeado pra `to_button_id` no backend)
 *   - `no-click`     → branch de timeout (transition direta, `to_button_id=NULL`)
 */
const WaitClickNode = ({ data, id, selected }: FlowNodeProps) => {
    const config = data.config || {};
    const waitSeconds = (config.delay_seconds as number) || 0;
    const behavior = data.wait_on_message_behavior || 'ignore';

    // ─── handles dinâmicos: lê o source via React Flow store ───────────
    // Encontra a edge incoming (qualquer sourceHandle) → resolve sourceNode
    // → extrai botões do tipo URL.
    // Tipamos como `any` pq a tipagem genérica do useStore não traz a shape
    // interna de edges/nodes do React Flow.
    const sourceButtons = useStore((store): ButtonData[] => {
        const edges: any[] = Array.from((store as any).edges ?? []);
        const incoming: any = edges.find((e: any) => e.target === id);
        if (!incoming) return [];

        const nodes: any[] = Array.from((store as any).nodeLookup?.values?.() ?? (store as any).nodes ?? []);
        const sourceNode: any = nodes.find((n: any) => n.id === incoming.source);
        if (!sourceNode) return [];

        // Aceita buttons da raiz ou do config (padrões variam por tipo de node)
        const raw = (sourceNode.data?.buttons || sourceNode.data?.config?.buttons || []) as ButtonData[];
        // Wait_click só faz sentido pra botões URL — postback tem fluxo nativo
        return raw.filter((b) => b.button_type === 'url');
    });

    const formatWait = (s: number) => {
        if (s >= 86400) return { v: Math.floor(s / 86400), unit: s >= 172800 ? 'dias' : 'dia', Icon: Calendar };
        if (s >= 3600) return { v: Math.floor(s / 3600), unit: s >= 7200 ? 'horas' : 'hora', Icon: Clock };
        if (s >= 60) return { v: Math.floor(s / 60), unit: s >= 120 ? 'minutos' : 'minuto', Icon: Clock };
        return { v: s, unit: s === 1 ? 'segundo' : 'segundos', Icon: Timer };
    };

    const behaviorLabel: Record<string, string> = {
        ignore: 'ignora mensagem',
        restart: 'reinicia o flow',
        freeze: 'congela tudo',
    };

    const tInfo = formatWait(waitSeconds);
    const TIcon = tInfo.Icon;

    return (
        <div
            className={`
                bg-white rounded-lg border-l-[3px] border-l-orange-500 shadow-sm min-w-[260px] max-w-[300px]
                transition-all duration-150
                ${selected ? 'shadow-md ring-2 ring-blue-400/40' : 'border-t border-r border-b border-gray-200 hover:shadow-md'}
            `}
        >
            {/* Header */}
            <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-orange-50 rounded-lg flex items-center justify-center">
                        <MousePointerClick className="w-3.5 h-3.5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[13px] text-gray-800 block truncate">
                            {data.node_name || 'Esperar Click'}
                        </span>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">
                            Wait For Click
                        </span>
                    </div>
                </div>
            </div>

            {/* Tempo de espera */}
            <div className="px-3 pt-3">
                {waitSeconds > 0 ? (
                    <div className="flex items-center gap-2.5 bg-orange-50/60 border border-orange-100 rounded-md px-2.5 py-2">
                        <TIcon className="w-4 h-4 text-orange-600 flex-shrink-0" />
                        <div className="flex-1">
                            <div className="text-[13px] font-bold text-gray-800 leading-tight">
                                {tInfo.v} {tInfo.unit}
                            </div>
                            <div className="text-[10px] text-gray-500">tempo de espera</div>
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-2 bg-amber-50 border border-amber-100 rounded-md">
                        <p className="text-[11px] text-amber-700">⚠️ Sem tempo configurado</p>
                    </div>
                )}
            </div>

            {/* Comportamento msg */}
            <div className="px-3 pt-2 pb-2">
                <div className="text-[10px] text-gray-500">
                    Se receber mensagem: <span className="font-medium text-gray-700">{behaviorLabel[behavior] || behavior}</span>
                </div>
            </div>

            {/* Branches: 1 por botão URL + 1 "não clicou" */}
            <div className="px-3 pb-3 space-y-1.5">
                {sourceButtons.length === 0 ? (
                    <div className="text-[10px] text-gray-400 border border-dashed border-gray-200 rounded p-2 text-center">
                        Conecte este node a um Template com botões URL pra ver os ramos
                    </div>
                ) : (
                    sourceButtons.map((btn, idx) => (
                        <div
                            key={btn.id || idx}
                            className="relative text-[11px] bg-orange-50 border border-orange-100 rounded-md px-3 py-2 text-orange-700 font-medium flex items-center gap-1.5 min-h-[32px] overflow-visible"
                        >
                            <LinkIcon className="w-3 h-3 text-orange-600 flex-shrink-0" />
                            <span className="truncate flex-1">
                                Clicou: {btn.button_text || `Botão ${idx + 1}`}
                            </span>
                            <Handle
                                type="source"
                                position={Position.Right}
                                id={`button-${btn.id}`}
                                style={{
                                    position: 'absolute',
                                    right: '-10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    width: '14px',
                                    height: '14px',
                                    background: '#fb923c',
                                    border: '2px solid white',
                                    borderRadius: '50%',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    cursor: 'crosshair',
                                    zIndex: 10,
                                }}
                            />
                        </div>
                    ))
                )}

                {/* Handle de "não clicou" — sempre presente */}
                <div className="relative text-[11px] bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-gray-600 font-medium flex items-center gap-1.5 min-h-[32px] overflow-visible">
                    <Hourglass className="w-3 h-3 text-gray-500 flex-shrink-0" />
                    <span className="truncate flex-1">Não clicou (timeout)</span>
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="no-click"
                        style={{
                            position: 'absolute',
                            right: '-10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '14px',
                            height: '14px',
                            background: '#9ca3af',
                            border: '2px solid white',
                            borderRadius: '50%',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            cursor: 'crosshair',
                            zIndex: 10,
                        }}
                    />
                </div>
            </div>

            {/* Actions */}
            <div className="border-t border-gray-100 px-3 py-1.5 flex gap-1">
                <Button size="sm" variant="ghost" className="flex-1 text-gray-500 hover:text-orange-600 hover:bg-orange-50/50 h-7 text-[11px]" onClick={() => data.onEdit?.(data.id)}>
                    <Settings className="w-3 h-3 mr-1" />
                    Editar
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-blue-500 hover:bg-blue-50/50 h-7 px-2" title="Duplicar" onClick={(e) => { e.stopPropagation(); data.onDuplicate?.(data.id); }}>
                    <Copy className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-red-500 hover:bg-red-50/50 h-7 px-2" onClick={() => data.onDelete?.(data.id)}>
                    <Trash2 className="w-3 h-3" />
                </Button>
            </div>

            {/* Handle de entrada (recebe edge do source) */}
            <Handle
                type="target"
                id="target"
                position={Position.Left}
                className="!w-3.5 !h-3.5 !bg-orange-500 !border-2 !border-white"
                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
            />
        </div>
    );
};

export default memo(WaitClickNode);
